import React, { useState, ChangeEvent, useEffect } from 'react';
import { debounce } from '@/utils/helpers';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoTone from '@mui/icons-material/DeleteTwoTone';
import ContentCopyTwoTone from '@mui/icons-material/ContentCopyTwoTone';
import PriceChangeTwoTone from '@mui/icons-material/PriceChangeTwoTone';
import {
  Box,
  Card,
  CardHeader,
  Checkbox,
  Container,
  Divider,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
  alpha,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  Chip
} from '@mui/material';
import BulkActions from '@/components/BulkActions';
import StatusLabel from '@/components/StatusLabel';
import ProductTypeLabel from '@/components/ProductTypeLabel';
import StatusFilter from '@/components/filters/StatusFilter';
import ImagePreviewModal from '@/components/images/ImagePreviewModal';
import CustomImageComponent from '@/components/images/CustomImageComponent';
import ImagePlaceholder from '@/components/images/ImagePlaceholder';
import DateFilterBar from '@/components/filters/DateFilterBar';
import { DateRange } from '@/types/DateRange.interface';
import CompanyProductDialog from '@/components/modals/CompanyProductDialog';
import { allergenService } from '@/data/allergenService';
import FilterPopover, {
  FilterOption
} from '@/components/filters/FilterPopover';
import CategoryFilter from '@/components/filters/CategoryFilter';
import LocationFilter from '@/components/filters/LocationFilter';
import PriceRangeFilter from '@/components/filters/PriceRangeFilter';
import ProductTypeFilter from '@/components/filters/ProductTypeFilter';
import { useTableFilters } from '@/hooks/useTableFilters';
import { rowOpenProps } from '@/utils/rowClick';

import {
  BranchOverride,
  CompanyProduct
} from '@/types/CompanyProduct.interface';
import { companyProductService } from '@/data/companyProductService';
import BranchOverrideDialog from '@/pages/management/company-products/detail/dialogs/BranchOverrideDialog';
import { formatDateToDayMonthYearTime } from '@/utils/dateFormatters';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import NoDataFound from '@/components/NoDataFound';
import {
  Add,
  SearchOutlined,
  Store as StoreIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { Filters } from '@/types/Filters';
import { useTranslation } from 'react-i18next';
import { user$ } from '@/store/userStore';
import { format } from 'date-fns';
import { Role } from '@/enums/role';
import BulkStatusUpdateDialog from '@/components/modals/BulkStatusUpdateDialog';
import SortLabel from '@/components/SortLabel';
import type { SortOrder } from '@/utils/listSort';
import { getCompanyProductStatusOptions } from '@/utils/statusOptions';
import { useNavigate } from 'react-router-dom';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';

type CompanyProductStatus = 'ACTIVE' | 'PASSIVE' | 'PENDING' | 'DELETED';

interface ProductsTableProps {
  products: CompanyProduct[];
  loading: boolean;
  totalCount: number;
  setShowNewProductDialog?: (show: boolean) => void;
  onDeleteProduct?: (productId: number) => void;
  /** "Kopyala": opens the new-product form prefilled from this product. */
  onCopyProduct?: (product: CompanyProduct) => void;
  onBulkDeleteProducts?: (
    products: CompanyProduct[],
    onSuccess?: () => void
  ) => Promise<void>;
  onBulkUpdateStatus?: (
    products: CompanyProduct[],
    status: string,
    onSuccess?: () => void
  ) => Promise<void>;
  /** "Toplu fiyat": opens the price dialog for the selected products. */
  onBulkPrice?: (products: CompanyProduct[], onSuccess: () => void) => void;
  onUpdateProduct?: (
    productId: number,
    updates: {
      status?: CompanyProductStatus;
      basePrice?: number;
      name?: string;
      description?: string;
      categoryId?: number;
      imageFile?: File;
      isMenu?: boolean;
      allowNegativeStock?: boolean;
      isStockTracked?: boolean;
      trackExpiry?: boolean;
      stockUnit?: string;
      allergens?: string[];
      allergenNote?: string;
      companyId: number;
    }
  ) => Promise<void>;
  onFilterChange: (filters: Filters) => void;
  customActions?: (product: CompanyProduct) => React.ReactNode | null;
  tableTitle?: string;
  hideAddButton?: boolean;
  addButtonText?: string;
  customButtons?: any[];
  notApplyPadding?: boolean;
  hideDeleteButton?: boolean;
  rowsPerPageOptions?: number[];
  limit?: number;
  hideColumns?: string[];
  onSelectedProductsChange?: (products: CompanyProduct[]) => void;
  pageKey?: string;
  /** Keep the applied filters and page in the address bar. */
  syncUrl?: boolean;
  /** Clicking a row opens the product (not in selection dialogs). */
  openRowDetails?: boolean;
  /** Name, price and date headers sort the list (GET /company-products). */
  sortable?: boolean;
  /**
   * Whether branch overrides are allowed by company settings
   * (allowBranchOverrides && strategy !== CENTRALIZED). Controls the
   * branch-admin customize action on company-wide products.
   */
  overridesEnabled?: boolean;
}

const ProductsTable = ({
  products,
  loading,
  totalCount,
  onFilterChange,
  setShowNewProductDialog,
  onDeleteProduct,
  onCopyProduct,
  onBulkDeleteProducts,
  onBulkUpdateStatus,
  onBulkPrice,
  onUpdateProduct,
  customActions,
  tableTitle,
  hideAddButton = false,
  addButtonText,
  customButtons = [],
  notApplyPadding = false,
  hideDeleteButton = false,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS,
  limit: initialLimit,
  hideColumns = [],
  onSelectedProductsChange,
  pageKey,
  syncUrl,
  openRowDetails = false,
  overridesEnabled = true,
  sortable = false
}: ProductsTableProps) => {
  const [selectedProducts, setSelectedProducts] = useState<CompanyProduct[]>(
    []
  );
  const selectedBulkActions = selectedProducts.length > 0;
  const globalUserState = user$.get();
  const userRole = globalUserState.role;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<CompanyProduct | null>(
    null
  );
  const [editError, setEditError] = useState<string | undefined>(undefined);
  const [showStatusUpdateDialog, setShowStatusUpdateDialog] = useState(false);
  const [statusUpdateSelectedProducts, setStatusUpdateSelectedProducts] =
    useState<CompanyProduct[]>([]);

  const isSuperAdmin = userRole === Role.SUPER_ADMIN;
  const isBranchAdmin = userRole === Role.BRANCH_ADMIN;
  const currentBranch = globalUserState.currentBranch;
  const isAdminView = !currentBranch;
  const showLocationColumn = isSuperAdmin && isAdminView;

  // Branch admins cannot update/delete company-wide products (products without
  // createdByBranchId); they can only customize them via branch overrides.
  const usesOverrideEdit = (product: CompanyProduct) =>
    isBranchAdmin && product.createdByBranchId == null;

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideProduct, setOverrideProduct] = useState<CompanyProduct | null>(
    null
  );
  const [overrideToEdit, setOverrideToEdit] = useState<BranchOverride | null>(
    null
  );
  const [existingOverrides, setExistingOverrides] = useState<BranchOverride[]>(
    []
  );

  useEffect(() => {
    if (onSelectedProductsChange) {
      onSelectedProductsChange(selectedProducts);
    }
  }, [selectedProducts, onSelectedProductsChange]);

  // State for price range filter
  const {
    filters,
    setFilters,
    handleSearch: debouncedSearch,
    handleStatusChange,
    handleCategoryChange,
    handleCompanyChange,
    handleDateRangeChange: handleDateChange,
    handleProductTypeChange,
    handleSortChange,
    handleResetFilters,
    getActiveFiltersCount
  } = useTableFilters({
    initialFilters: {
      limit: initialLimit || 10,
      page: 0
    },
    onFilterChange,
    pageKey,
    syncUrl
  });
  const page = filters.page ?? 0;
  const limit = filters.limit ?? initialLimit ?? 10;
  const [minPrice, setMinPrice] = useState<string>(
    filters.price?.min !== undefined ? String(filters.price.min) : ''
  );
  const [maxPrice, setMaxPrice] = useState<string>(
    filters.price?.max !== undefined ? String(filters.price.max) : ''
  );

  // Header id → the API's sort column and first direction. In a branch the
  // list shows the branch's price, which the server cannot sort by.
  const sortableHeaders: Record<string, { column: string; first: SortOrder }> =
    {
      name: { column: 'name', first: 'asc' },
      ...(isAdminView &&
        !filters.branchId && { price: { column: 'price', first: 'desc' } }),
      createdAt: { column: 'createdAt', first: 'desc' }
    };

  const tableHeaders = [
    {
      id: 'checkbox',
      label: '',
      align: 'left',
      padding: 'checkbox'
    },
    {
      id: 'image',
      label: t('image'),
      align: 'center'
    },
    {
      id: 'name',
      label: t('name'),
      align: 'left'
    },
    {
      id: 'type',
      label: t('type'),
      align: 'center'
    },
    {
      id: 'category',
      label: t('category'),
      align: 'left'
    },
    {
      id: 'price',
      label: t('price'),
      align: 'left'
    },
    ...(showLocationColumn
      ? [
          {
            id: 'location',
            label: t('location'),
            align: 'left'
          }
        ]
      : []),
    {
      id: 'status',
      label: t('status'),
      align: 'center'
    },
    {
      id: 'createdAt',
      label: t('created.at'),
      align: 'left'
    },
    {
      id: 'updatedAt',
      label: t('updated.at'),
      align: 'left'
    },
    {
      id: 'actions',
      label: t('actions'),
      align: 'center'
    }
  ].filter((header) => !hideColumns.includes(header.id));

  const handleSelectAllProducts = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    event.stopPropagation();
    setSelectedProducts(event.target.checked ? products : []);
  };

  const handleSelectOneProduct = (
    event: ChangeEvent<HTMLInputElement> | null,
    productId: number
  ): void => {
    if (event) {
      event.stopPropagation();
    }

    const product = products.find((u) => u.id === productId);
    if (!product) return;

    if (!selectedProducts.find((u) => u.id === productId)) {
      setSelectedProducts((prevSelected) => [...prevSelected, product]);
    } else {
      setSelectedProducts((prevSelected) =>
        prevSelected.filter((u) => u.id !== productId)
      );
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    setFilters((prev) => ({ ...prev, page: newPage, limit }));
    onFilterChange({
      ...filters,
      page: newPage,
      limit
    });
  };

  const handleLimitChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newLimit = parseInt(event.target.value);
    setFilters((prev) => ({ ...prev, page: 0, limit: newLimit }));
    onFilterChange({
      ...filters,
      page: 0,
      limit: newLimit
    });
  };

  const visibleSelectedCount = products.filter((product) =>
    selectedProducts.some((selected) => selected.id === product.id)
  ).length;
  const selectedSomeProducts =
    visibleSelectedCount > 0 && visibleSelectedCount < products.length;
  const selectedAllProducts =
    products.length > 0 && visibleSelectedCount === products.length;
  const theme = useTheme();

  const handleBulkDelete = async (products: CompanyProduct[]) => {
    return onBulkDeleteProducts?.(products, () => {
      setSelectedProducts([]);
    });
  };

  const handleBulkStatusUpdate = (products: CompanyProduct[]) => {
    setStatusUpdateSelectedProducts(products);
    setShowStatusUpdateDialog(true);
  };

  const handleStatusUpdateConfirm = async (status: string) => {
    if (onBulkUpdateStatus && statusUpdateSelectedProducts.length > 0) {
      await onBulkUpdateStatus(statusUpdateSelectedProducts, status, () => {
        setSelectedProducts([]);
        setStatusUpdateSelectedProducts([]);
      });
    }
  };

  const handleSearch = debounce((value: string) => {
    debouncedSearch(value);
  }, 500);

  const handleEditClick = async (product: CompanyProduct) => {
    // Branch admins edit company-wide products through a branch override
    // (customization) instead of the direct product update endpoint,
    // which would reject them with a 404.
    if (usesOverrideEdit(product)) {
      if (!overridesEnabled) return;

      let overrides: BranchOverride[] = [];
      try {
        const result = await companyProductService.getBranchOverrides(
          product.id
        );
        overrides = Array.isArray(result) ? result : [];
      } catch (error) {
        console.error('Error fetching branch overrides:', error);
      }

      setExistingOverrides(overrides);
      setOverrideToEdit(
        overrides.find((o) => o.branchId === currentBranch?.id) || null
      );
      setOverrideProduct(product);
      setOverrideDialogOpen(true);
      return;
    }

    setSelectedProduct(product);
    setEditDialogOpen(true);
  };

  const handleOverrideDialogClose = () => {
    setOverrideDialogOpen(false);
    setOverrideProduct(null);
    setOverrideToEdit(null);
    setExistingOverrides([]);
  };

  const handleOverrideSaved = () => {
    handleOverrideDialogClose();
    onFilterChange({
      ...filters,
      page,
      limit
    });
  };

  const handleViewDetails = (product: CompanyProduct) => {
    navigate(`/management/company-products/${product.id}`);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedProduct(null);
    setEditError(undefined);
  };

  const handleSave = async (productData: {
    name: string;
    description: string;
    basePrice: string;
    costPrice?: string;
    vatRate?: number | null;
    pointValue?: number;
    categoryId?: number;
    imageFile?: File;
    status: CompanyProductStatus;
    isMenu: boolean;
    companyId: number;
    allowNegativeStock: boolean;
    isStockTracked: boolean;
    trackExpiry: boolean;
    stockUnit: string;
    allergens: string[];
    allergenNote: string;
  }) => {
    if (!selectedProduct) return;

    try {
      const updatesToSave = {
        name: productData.name,
        description: productData.description,
        basePrice: parseFloat(productData.basePrice) || 0,
        costPrice: productData.costPrice
          ? parseFloat(productData.costPrice)
          : undefined,
        vatRate: productData.vatRate,
        pointValue: productData.pointValue,
        categoryId: productData.categoryId,
        status: productData.status,
        isMenu: productData.isMenu,
        companyId: productData.companyId,
        allowNegativeStock: productData.allowNegativeStock,
        isStockTracked: productData.isStockTracked,
        trackExpiry: productData.trackExpiry,
        stockUnit: productData.stockUnit,
        allergens: productData.allergens,
        allergenNote: productData.allergenNote,
        ...(productData.imageFile && { imageFile: productData.imageFile })
      };

      await onUpdateProduct?.(selectedProduct.id, updatesToSave);
      handleDialogClose();
    } catch (error) {
      console.error('Error updating product: ', error);
      setEditError(t('products.update.error.message'));
      throw error;
    }
  };

  // Rozet tooltip'inde alerjen kodlarını Türkçe adlara çevirmek için katalog
  const [allergenLabels, setAllergenLabels] = useState<Record<string, string>>(
    {}
  );
  useEffect(() => {
    allergenService
      .getLabelMap()
      .then(setAllergenLabels)
      .catch((err) => console.error('Error fetching allergen labels:', err));
  }, []);

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  const handleImageClick = (imageUrl: string) => {
    if (imageUrl) {
      setPreviewImageUrl(imageUrl);
      setPreviewModalOpen(true);
    }
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
  };

  const deleteButton = hideDeleteButton
    ? null
    : {
        label: 'delete',
        icon: <DeleteTwoTone />,
        color: 'error',
        onClick: handleBulkDelete,
        showCondition: true,
        disabled: selectedProducts.length === 0,
        position: 'left',
        showConfirmDialog: true,
        confirmTitle: t('confirm.bulk.delete'),
        confirmMessage: t('confirm.bulk.delete.question'),
        variant: 'contained'
      };

  const statusUpdateButton = onBulkUpdateStatus
    ? {
        label: 'change.status',
        icon: <EditTwoToneIcon />,
        color: 'primary',
        onClick: handleBulkStatusUpdate,
        showCondition: true,
        disabled: selectedProducts.length === 0,
        position: 'left',
        variant: 'contained'
      }
    : null;

  const bulkPriceButton = onBulkPrice
    ? {
        label: 'bulkPrice.button',
        icon: <PriceChangeTwoTone />,
        color: 'primary',
        onClick: async (items: CompanyProduct[]) =>
          onBulkPrice(items, () => setSelectedProducts([])),
        showCondition: true,
        disabled: selectedProducts.length === 0,
        position: 'left',
        variant: 'outlined'
      }
    : null;

  const allButtons = [
    ...(deleteButton ? [deleteButton] : []),
    ...(statusUpdateButton ? [statusUpdateButton] : []),
    ...(bulkPriceButton ? [bulkPriceButton] : []),
    ...customButtons
  ];

  const handleDateRangeChange = (dateRange: DateRange | null): void => {
    if (dateRange === null) {
      handleDateChange(undefined, undefined);
      return;
    }

    const timezone =
      user$.currentBranch.get()?.timezone ??
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    handleDateChange(
      format(dateRange.startDate, 'yyyy-MM-dd'),
      format(dateRange.endDate, 'yyyy-MM-dd'),
      timezone
    );
  };

  // Filter options for the popover
  const filterOptions: FilterOption[] = [
    {
      id: 'status',
      label: t('filters.status'),
      component: (
        <StatusFilter
          value={filters.status}
          onChange={handleStatusChange}
          size="small"
        />
      )
    },
    {
      id: 'date',
      label: t('filters.date.range'),
      component: (
        <DateFilterBar
          onChange={handleDateRangeChange}
          filterLabel={t('filters.date.range')}
          compact
          showClearButton
          noFilterLabel={t('filters.date.all')}
          initialDateRange={
            filters.startDate && filters.endDate
              ? {
                  startDate: new Date(filters.startDate),
                  endDate: new Date(filters.endDate)
                }
              : undefined
          }
        />
      )
    },
    {
      id: 'type',
      label: t('filters.product.type'),
      component: (
        <ProductTypeFilter
          value={filters.type}
          onChange={handleProductTypeChange}
          size="small"
        />
      )
    },
    {
      id: 'category',
      label: t('filters.category'),
      component: (
        <CategoryFilter
          value={filters.categoryId}
          onChange={handleCategoryChange}
          size="small"
        />
      )
    },
    {
      id: 'location',
      label: t('filters.location'),
      component: (
        <LocationFilter
          companyId={filters.companyId}
          onBranchChange={() => {}}
          onCompanyChange={handleCompanyChange}
          size="small"
          hideBranchFilter={true}
        />
      )
    },
    {
      id: 'price',
      label: t('filters.price'),
      component: (
        <PriceRangeFilter
          minPrice={minPrice}
          maxPrice={maxPrice}
          onMinPriceChange={setMinPrice}
          onMaxPriceChange={setMaxPrice}
          size="small"
        />
      )
    }
  ];

  const handleApplyWithPrice = () => {
    // Update price filter before applying
    const min = minPrice ? parseFloat(minPrice) : undefined;
    const max = maxPrice ? parseFloat(maxPrice) : undefined;
    // Apply all filters including price
    const appliedFilters = {
      ...filters,
      price: { min, max },
      page: 0
    };
    setFilters(appliedFilters);
    onFilterChange(appliedFilters);
  };

  const handleResetAll = () => {
    // Reset price inputs
    setMinPrice('');
    setMaxPrice('');
    // Reset all filters
    handleResetFilters();
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        maxWidth: notApplyPadding ? '100%' : '90%',
        p: notApplyPadding ? 0 : undefined
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Card>
            {selectedBulkActions && (
              <Box flex={1} p={2} className="product-bulk-actions">
                <BulkActions<CompanyProduct>
                  selected={selectedProducts}
                  buttons={allButtons}
                />
              </Box>
            )}
            {!selectedBulkActions && (
              <CardHeader
                action={
                  <Box
                    display="flex"
                    alignItems="center"
                    width="100%"
                    className="product-filters"
                  >
                    <FormControl sx={{ minWidth: 200, mr: 2 }}>
                      <OutlinedInput
                        placeholder={`${t('search')}...`}
                        defaultValue={filters.search}
                        onChange={(e) => handleSearch(e.target.value)}
                        startAdornment={
                          <InputAdornment position="start">
                            <SearchOutlined />
                          </InputAdornment>
                        }
                        size="small"
                      />
                    </FormControl>

                    <FilterPopover
                      filterOptions={filterOptions}
                      onApplyFilters={handleApplyWithPrice}
                      onResetFilters={handleResetAll}
                      activeFiltersCount={getActiveFiltersCount()}
                    />

                    {!hideAddButton && (
                      <Button
                        sx={{ ml: 2 }}
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setShowNewProductDialog?.(true)}
                        className="product-add-button"
                      >
                        {addButtonText || t('new.product')}
                      </Button>
                    )}
                  </Box>
                }
                title={
                  <Box display="flex" alignItems="center">
                    <Typography variant="h4" component="span">
                      {tableTitle || t('product.list')}
                    </Typography>
                    <Tooltip arrow title={t('refresh.list')}>
                      <IconButton
                        onClick={() => {
                          onFilterChange({
                            ...filters,
                            page,
                            limit
                          });
                        }}
                        sx={{
                          ml: 1,
                          '&:hover': {
                            background: theme.colors.primary.lighter
                          },
                          color: theme.palette.primary.main
                        }}
                        className="product-refresh"
                      >
                        <RefreshTwoToneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
            )}
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell
                        key={header.id}
                        align={header.align as 'left' | 'center' | 'right'}
                        padding={
                          header.padding as 'checkbox' | 'none' | 'normal'
                        }
                      >
                        {header.id === 'checkbox' ? (
                          <Checkbox
                            color="primary"
                            checked={selectedAllProducts}
                            indeterminate={selectedSomeProducts}
                            onChange={handleSelectAllProducts}
                          />
                        ) : sortable && sortableHeaders[header.id] ? (
                          <SortLabel
                            column={sortableHeaders[header.id].column}
                            firstOrder={sortableHeaders[header.id].first}
                            label={header.label}
                            sort={filters}
                            onSort={handleSortChange}
                          />
                        ) : (
                          header.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length}>
                        <Box p={2} display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : products.length > 0 ? (
                    products.map((product) => {
                      const isProductSelected = selectedProducts.some(
                        (u) => u.id === product.id
                      );
                      return (
                        <TableRow
                          hover
                          key={product.id}
                          selected={isProductSelected}
                          {...(openRowDetails
                            ? rowOpenProps(() => handleViewDetails(product))
                            : {})}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isProductSelected}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>
                              ) => handleSelectOneProduct(event, product.id)}
                              value={isProductSelected}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ mx: 0, px: 0 }}
                            className="product-image"
                          >
                            {product.file?.url ? (
                              <CustomImageComponent
                                imageUrl={product.file.url}
                                alt={product.name}
                                onClick={handleImageClick}
                                width={54}
                                height={54}
                              />
                            ) : (
                              <Tooltip
                                title={
                                  usesOverrideEdit(product)
                                    ? product.isOverridden
                                      ? t('edit.branch.override')
                                      : t('create.branch.override')
                                    : t('edit.product')
                                }
                                arrow
                              >
                                <Box onClick={() => handleEditClick(product)}>
                                  <ImagePlaceholder width={54} height={54} />
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                color="text.primary"
                                gutterBottom
                                noWrap
                              >
                                {product.name}
                              </Typography>
                              {(product.allergens?.length ||
                                product.allergenNote) && (
                                <Tooltip
                                  arrow
                                  title={
                                    <Box>
                                      {(product.allergens || []).map((code) => (
                                        <Typography
                                          key={code}
                                          variant="body2"
                                          component="div"
                                        >
                                          • {allergenLabels[code] || code}
                                        </Typography>
                                      ))}
                                      {product.allergenNote && (
                                        <Typography
                                          variant="body2"
                                          component="div"
                                          sx={{ fontStyle: 'italic', mt: 0.5 }}
                                        >
                                          {product.allergenNote}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                >
                                  <Chip
                                    label={`⚠ ${t('product.allergens.badge')}`}
                                    color="warning"
                                    variant="outlined"
                                    size="small"
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>

                          {!hideColumns.includes('type') && (
                            <TableCell align="center">
                              <ProductTypeLabel isMenu={product.isMenu} />
                            </TableCell>
                          )}

                          {!hideColumns.includes('category') && (
                            <TableCell>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                color="text.primary"
                                gutterBottom
                                noWrap
                              >
                                {product.category?.name || '-'}
                              </Typography>
                            </TableCell>
                          )}

                          {!hideColumns.includes('price') && (
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {(product.price ?? product.basePrice)
                                    ? `${product.price ?? product.basePrice} TL`
                                    : '-'}
                                </Typography>
                                {product.isOverridden && (
                                  <Tooltip
                                    title={`${t('base.price')}: ${
                                      product.basePrice
                                    } TL`}
                                    arrow
                                  >
                                    <StoreIcon
                                      sx={{
                                        fontSize: 16,
                                        color: theme.palette.info.main
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          )}

                          {showLocationColumn && (
                            <TableCell>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                color="text.primary"
                                gutterBottom
                                noWrap
                              >
                                {product.company?.name || '-'}
                              </Typography>
                            </TableCell>
                          )}

                          {!hideColumns.includes('status') && (
                            <TableCell align="center">
                              <StatusLabel status={product?.status} />
                            </TableCell>
                          )}

                          {!hideColumns.includes('createdAt') && (
                            <TableCell>
                              {formatDateToDayMonthYearTime(product?.createdAt)}
                            </TableCell>
                          )}

                          {!hideColumns.includes('updatedAt') && (
                            <TableCell>
                              {formatDateToDayMonthYearTime(product?.updatedAt)}
                            </TableCell>
                          )}

                          {!hideColumns.includes('actions') && (
                            <TableCell
                              align="center"
                              className="product-actions"
                            >
                              {customActions ? (
                                customActions(product)
                              ) : (
                                <>
                                  <Tooltip title={t('view.details')} arrow>
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewDetails(product);
                                      }}
                                      sx={{
                                        '&:hover': {
                                          background: theme.colors.info.lighter
                                        },
                                        color: theme.palette.info.main
                                      }}
                                      color="inherit"
                                      size="small"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {(!usesOverrideEdit(product) ||
                                    overridesEnabled) && (
                                    <Tooltip
                                      title={
                                        usesOverrideEdit(product)
                                          ? product.isOverridden
                                            ? t('edit.branch.override')
                                            : t('create.branch.override')
                                          : t('edit.product')
                                      }
                                      arrow
                                    >
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditClick(product);
                                        }}
                                        sx={{
                                          '&:hover': {
                                            background:
                                              theme.colors.primary.lighter
                                          },
                                          color: theme.palette.primary.main
                                        }}
                                        color="inherit"
                                        size="small"
                                      >
                                        {usesOverrideEdit(product) ? (
                                          <StoreIcon
                                            sx={{
                                              color: (theme) =>
                                                theme.palette.primary.main,
                                              fontSize: 20
                                            }}
                                          />
                                        ) : (
                                          <EditTwoToneIcon
                                            sx={{
                                              color: (theme) =>
                                                theme.palette.primary.main,
                                              fontSize: 20,
                                              '&:hover': {
                                                transform: 'scale(1.1)',
                                                transition: 'transform 0.2s'
                                              }
                                            }}
                                          />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {onCopyProduct && !hideAddButton && (
                                    <Tooltip title={t('copy.product')} arrow>
                                      <IconButton
                                        aria-label={t('copy.product')}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onCopyProduct(product);
                                        }}
                                        sx={{
                                          '&:hover': {
                                            background: alpha(
                                              theme.palette.secondary.main,
                                              0.1
                                            )
                                          },
                                          color: theme.palette.secondary.main
                                        }}
                                        color="inherit"
                                        size="small"
                                      >
                                        <ContentCopyTwoTone fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {!usesOverrideEdit(product) && (
                                    <Tooltip title={t('delete.product')} arrow>
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDeleteProduct?.(product.id);
                                        }}
                                        sx={{
                                          '&:hover': {
                                            background:
                                              theme.colors.error.lighter
                                          },
                                          color: theme.palette.error.main
                                        }}
                                        color="inherit"
                                        size="small"
                                      >
                                        <DeleteTwoTone fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <NoDataFound
                      message={t('no.product.found')}
                      colSpan={tableHeaders.length}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box p={2}>
              <TablePagination
                component="div"
                count={totalCount || 0}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={totalCount ? page : 0}
                rowsPerPage={limit}
                rowsPerPageOptions={rowsPerPageOptions}
                className="product-pagination"
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
      <ImagePreviewModal
        open={previewModalOpen}
        onClose={handleClosePreviewModal}
        imageUrl={previewImageUrl}
        alt={selectedProduct?.name || 'Product image'}
      />

      <CompanyProductDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        product={selectedProduct}
        error={editError}
      />
      {overrideProduct && (
        <BranchOverrideDialog
          open={overrideDialogOpen}
          onClose={handleOverrideDialogClose}
          onSave={handleOverrideSaved}
          product={overrideProduct}
          override={overrideToEdit}
          existingOverrides={existingOverrides}
        />
      )}
      <BulkStatusUpdateDialog
        open={showStatusUpdateDialog}
        onClose={() => {
          setShowStatusUpdateDialog(false);
          setStatusUpdateSelectedProducts([]);
        }}
        onConfirm={handleStatusUpdateConfirm}
        selectedItems={statusUpdateSelectedProducts}
        statusOptions={getCompanyProductStatusOptions()}
        title={t('bulk.status.update.products.title')}
        description={t('bulk.status.update.products.description')}
        itemDisplayProperty="name"
        currentStatusProperty="status"
      />
    </Container>
  );
};

export default ProductsTable;
